// Ported from cartadigital-pos app/domain/pricing/totals.ts — keep in
// behavioral parity with backend OrderUtils.getOrderTotals + V2OrdersService.
import type {
  ICouponDiscountMetadata,
  IPosCartLine,
  IPosDiscountInput,
  IPosOrderTotals,
} from '../pos-types';
import { OrderType } from '@/enums/OrderEnums';
import { RoundMode } from '@/enums/RoundMode';
import { TaxDisplayMode } from '@/enums/TaxDisplayMode';
import { roundPrice } from '@/utils';
import { applyRound } from './rounding';

export interface IComputeOrderTotalsInput {
  type: OrderType;
  cartItems: readonly IPosCartLine[];
  discounts?: readonly IPosDiscountInput[] | null;
  /**
   * Delivery cost BEFORE tax (zone/base already resolved).
   */
  deliveryCost: number;
  taxPercent: number | null | undefined;
  taxMode: TaxDisplayMode;
  taxDelivery: boolean;
  roundMode: RoundMode;
  roundInterval: number;
}

function normalizeMoney(value: unknown): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) return 0;
  return value;
}

function computeDiscountedAmount(params: {
  discounts: readonly IPosDiscountInput[];
  products: number;
  delivery: number;
}): number {
  if (!params.discounts.length) return 0;

  let discountedAmount = 0;
  const baseForDiscount = params.products + params.delivery;

  for (const discount of params.discounts) {
    const value = normalizeMoney(discount.value);

    if (discount.isPercentage) {
      // Percentage discounts apply only to products (same as backend V2).
      discountedAmount += roundPrice(params.products * (value / 100));

      const meta = discount.metadata as ICouponDiscountMetadata | null;
      if (typeof meta?.maxDiscountAmount === 'number') {
        discountedAmount = roundPrice(
          Math.min(discountedAmount, meta.maxDiscountAmount)
        );
      }
    } else {
      // Fixed discounts apply to products + delivery.
      discountedAmount += value;
    }
  }

  // Ensure discount doesn't exceed the total (same as backend).
  if (discountedAmount > baseForDiscount) {
    discountedAmount = baseForDiscount;
  }

  return discountedAmount;
}

/**
 * Compute POS order totals with parity to backend V2 equations:
 * - `OrderUtils.getOrderTotals()` for products/delivery taxes + discounts
 * - `V2OrdersService.handle()` for total composition + applyRound + roundDifference
 */
export function computeOrderTotals(
  input: IComputeOrderTotalsInput
): IPosOrderTotals {
  const type = input.type;
  const taxMode = input.taxMode;
  const taxPercent = input.taxPercent;
  const taxFactor =
    typeof taxPercent === 'number' &&
    Number.isFinite(taxPercent) &&
    taxPercent > 0 &&
    taxMode !== TaxDisplayMode.NoTax
      ? 1 + taxPercent / 100
      : null;

  let products = 0;
  let productsTax = 0;

  let delivery =
    type === OrderType.Delivery ? normalizeMoney(input.deliveryCost) : 0;
  let deliveryTax = 0;

  // ---------------------------------------------------------------------------
  // Products + productsTax
  // ---------------------------------------------------------------------------
  for (const ci of input.cartItems) {
    // Safety: allow callers to pass mixed active/canceled lines; totals must
    // exclude soft-canceled rows (mirrors backend behavior).
    if (ci.canceledAt != null) continue;

    const lineTotal = normalizeMoney(ci.totalPrice);
    products += lineTotal;

    if (taxFactor) {
      // POS line totals are already "partially taxed" like backend orderItems:
      // - AddAndShow/SilentAdd: lineTotal includes tax → extract included portion.
      // - AddAtInvoice: lineTotal excludes tax → compute added portion.
      const lineTax =
        taxMode === TaxDisplayMode.AddAtInvoice
          ? lineTotal * taxFactor - lineTotal
          : lineTotal - lineTotal / taxFactor;
      productsTax += lineTax;
    }
  }

  // ---------------------------------------------------------------------------
  // Delivery + deliveryTax
  // ---------------------------------------------------------------------------
  if (type === OrderType.Delivery && input.taxDelivery && taxFactor) {
    const baseDelivery = delivery;

    const taxedDelivery = baseDelivery * taxFactor;
    deliveryTax = taxedDelivery - baseDelivery;
    if (taxMode !== TaxDisplayMode.AddAtInvoice) delivery = taxedDelivery;
  }

  // ---------------------------------------------------------------------------
  // Discounts
  // ---------------------------------------------------------------------------
  const discountsArr = Array.isArray(input.discounts) ? input.discounts : [];
  const discountedAmount = computeDiscountedAmount({
    discounts: discountsArr,
    products,
    delivery,
  });

  // ---------------------------------------------------------------------------
  // Total composition + rounding interval
  // ---------------------------------------------------------------------------
  const taxAmount = roundPrice(productsTax + deliveryTax);
  const totalBeforeRound = roundPrice(
    products +
      delivery +
      (taxMode === TaxDisplayMode.AddAtInvoice ? taxAmount : 0) -
      discountedAmount
  );

  const roundedTotal = applyRound(
    totalBeforeRound,
    input.roundMode,
    input.roundInterval
  );

  const roundDifference =
    input.roundMode !== RoundMode.None
      ? roundPrice(roundedTotal - totalBeforeRound)
      : 0;

  return {
    products: roundPrice(products),
    delivery: roundPrice(delivery),
    productsTax: roundPrice(productsTax),
    deliveryTax: roundPrice(deliveryTax),
    discountedAmount: roundPrice(discountedAmount),
    total: roundPrice(roundedTotal),
    roundDifference: roundPrice(roundDifference),
  };
}
